<template>
  <div class="-mx-8 -mt-8 -mb-8 lg:-mx-12 lg:-mt-13 lg:-mb-11 goal-form-block-grid">
    <GoalImagePicker v-show="openImagePicker"
                     :active-image="form.goal_image"
                     @select="selectGoalImage"
                     @hide="hideImagePicker"/>
    <div v-show="!openImagePicker"
         class="p-12 grid grid-cols-2 lg:grid-cols-3 col-gap-8 row-gap-12 relative">
      <div class="col-span-2 lg:col-span-3 text-xl font-bold text-text leading-tight">{{ $t('formTitle') }}</div>
      <BaseSelect v-model="form.goal_type"
                  :options="goalTypeList"
                  :select-label="$t('typeLabel')"
                  :label="`name_${$i18n.locale}`"
                  class="flex-none"
                  :searchable="false"
                  :allow-empty="false"
                  @input="handleGoalType"/>
      <BaseInput v-model.trim="$v.form.goal_title.$model"
                 class="col-span-2"
                 :label="$t('goalNameLabel')"
                 :class="{'--error':$v.form.goal_title.$error}">
        <template v-slot:errors>
          <div class="error" v-if="!$v.form.goal_title.required">{{ $t('errors.required') }}</div>
        </template>
      </BaseInput>
      <div class="col-span-2 lg:col-span-3 grid grid-cols-3 col-gap-8 row-gap-4">
        <BaseSelect v-model="form.account"
                    :options="goalAccountsByGroup"
                    :select-label="$t('goalAccount')"
                    label="title"
                    track-by="id"
                    :group-label="'title'"
                    :group-values="'childs'"
                    :group-select="false"
                    :allow-empty="false"/>
        <BaseSelect v-if="!form.account.id" v-model="form.account_currency"
                    :options="currenciesList"
                    :select-label="$t('accountCurrency')"
                    :label="`name_${$i18n.locale}`"
                    group-label="title"
                    group-values="list"
                    :group-select="false"
                    :allow-empty="false"
                    :searchable="true"
                    :show-labels="false"/>
        <BaseSelect v-if="!form.account.id" v-model="form.account_type"
                    :options="accountTypeList"
                    :select-label="$t('accountType')"
                    track-by="id"
                    label="name"
                    :searchable="false"
                    :allow-empty="false"/>
        <div class="col-span-3 text-2sm text-text-ternary pr-10 self-center flex items-center">
          <font-awesome-icon v-if="form.account.id" icon="exclamation-triangle"
                             class="text-yellow mr-4"/>
          <div v-html="accountComputedTip"></div>
        </div>
      </div>
      <ValueInput v-model.trim="$v.form.goal_sum.$model"
                  :label="$t('goalSumLabel')"
                  :currency_id="goalCurrency.id"
                  text-align="left"
                  :class="{'--error':$v.form.goal_sum.$error}">
        <template v-slot:errors>
          <div class="error" v-if="!$v.form.goal_sum.required">{{ $t('errors.required') }}</div>
        </template>
      </ValueInput>
      <VueDatePicker v-model="form.date_start"
                     type="month"
                     :locale="{lang:$i18n.locale}"
                     color="#486680">
        <template #activator="{ date }">
          <div class="relative w-full">
            <BaseInput class="w-full cursor-pointer"
                       :label="$t('firstPaymentDate')"
                       readonly
                       v-model="date">
            </BaseInput>
            <IconCalendar class="text-border-hover absolute right-4 top-0 bottom-0 m-auto"/>
          </div>
        </template>
      </VueDatePicker>
      <VueDatePicker v-model="form.date_end"
                     type="month"
                     :min-date="minDateEnd"
                     :max-date="maxDateEnd"
                     :locale="{lang:$i18n.locale}"
                     color="#486680">
        <template #activator="{ date }">
          <div class="relative w-full">
            <BaseInput class="w-full cursor-pointer"
                       :label="$t('goalAchievementDate')"
                       readonly
                       v-model="date">
            </BaseInput>
            <IconCalendar class="text-border-hover absolute right-4 top-0 bottom-0 m-auto"/>
          </div>
        </template>
      </VueDatePicker>
      <BaseTextarea class="col-span-2 lg:col-span-3 one-line" v-model="form.description" :label="$t('noteLabel')"/>
      <BaseButton theme="red" class="lg:w-78" :loading="submitted" @click="submit">{{ $t('addGoal') }}</BaseButton>
    </div>
    <GoalCreatePreview v-bind="computedGoal"
                       :goal-image="form.goal_image.goal_image_url"
                       @toggleImagePicker="openImagePicker = !openImagePicker"/>
  </div>
</template>

<script>
import {VueDatePicker} from '@mathieustan/vue-datepicker';
import {mapActions, mapGetters} from "vuex";
import {required} from "vuelidate/lib/validators";
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";
import BaseInput from "@/components/UI/BaseInput/BaseInput";
import ValueInput from "@/components/UI/BaseInput/ValueInput";
import BaseTextarea from "@/components/UI/BaseTextarea";
import BaseButton from "@/components/UI/BaseButton";
import BaseRadioGroup from "@/components/UI/BaseRadioGroup";
import GoalImagePicker from "@/components/cabinet/Money/Goals/GoalImagePicker/GoalImagePicker";
import GoalCreatePreview from "@/components/cabinet/Money/Goals/GoalCreate/GoalCreatePreview";
import IconCalendar from "@/components/icons/IconCalendar";

export default {
  name: "GoalCreateForm",
  components: {
    IconCalendar,
    GoalCreatePreview,
    GoalImagePicker,
    BaseRadioGroup,
    BaseButton,
    BaseTextarea,
    ValueInput,
    BaseInput,
    BaseSelect,
    VueDatePicker
  },
  data() {
    return {
      form: {
        goal_type: null,
        goal_title: '',
        goal_image: {
          goal_image: null,
          goal_image_url: null,
          file: null,
        },
        goal_sum: null,
        date_start: null,
        date_end: null,
        description: '',

        account: null,
        account_type: null,
        account_currency: null,
      },
      computedGoal: {
        monthlyPayment: null,
        months: null,
        finishAt: null
      },
      submitted: false,
      openImagePicker: false
    }
  },
  validations: {
    form: {
      goal_title: {
        required
      },
      goal_sum: {
        required
      },
    },
  },
  computed: {
    ...mapGetters('space', ['spaceInfo', 'spaceCurrency']),
    ...mapGetters('space/accounts', ['accounts_by_groups']),
    ...mapGetters('cabinet/constants', ['getConstantList']),
    ...mapGetters('space/goals', ['goalTypeList']),
    ...mapGetters('cabinet/currencies', ['currenciesSortedList', 'displayCurrencyValue']),
    accountComputedTip() {
      if (this.form.account.id !== null) {
        return this.$t('warningAccountTip');
      }
      if (this.form.goal_type.id === 1) {
        return this.$t('savingAccountTip')
      } else {
        return this.$t('creditAccountTip')
      }
    },
    currenciesList() {
      return [...this.currenciesSortedList].map(item => {
        return {
          ...item,
          list: item.list.filter(curr => !curr.is_crypto)
        }
      })
    },
    minDateEnd() {
      return this.$moment(this.form.date_start, 'YYYY-MM').add(1, 'month').format('YYYY-MM');
    },
    maxDateEnd() {
      return this.$moment(this.form.date_start, 'YYYY-MM').add(100, 'year').format('YYYY-MM');
    },
    goalCurrency() {
      if (!!this.form.account && !!this.form.account.id) return this.form.account.currency;
      if (!!this.form.account && !this.form.account.id) return this.form.account_currency;
      return this.spaceCurrency;
    },
    goalAccountsByGroup() {
      let filteredAccountGroups = [...this.accounts_by_groups];

      if (this.form.goal_type && this.form.goal_type.id === 1) {
        let filterSavingsAccount = account => account.type_id !== 3 && account.type_id !== 5 && !account.has_goal && !account.currency.is_crypto
        filteredAccountGroups = filteredAccountGroups.map(group => ({
          ...group,
          childs: group.childs.filter(filterSavingsAccount)
        }))
      } else if (this.form.goal_type && this.form.goal_type.id === 2) {
        let filterCreditAccounts = account => (account.type_id === 3 || account.type_id === 5) && !account.has_goal && !account.currency.is_crypto
        filteredAccountGroups = filteredAccountGroups.map(group => ({
          ...group,
          childs: group.childs.filter(filterCreditAccounts)
        }))
      }
      return [
        {
          id: null,
          childs: [{
            id: null,
            title: this.$t('newAccountLabel'),
          }]
        },
        ...filteredAccountGroups
      ]
    },
    accountTypeList() {
      let typeList = this.getConstantList('account_type')
      if (!this.form.goal_type) return typeList;
      if (this.form.goal_type.id === 1) {
        return typeList.filter(item => item.id !== '3' && item.id !== '5')
      } else if (this.form.goal_type.id === 2) {
        return typeList.filter(item => item.id === '3' || item.id === '5')
      }
    },
  },
  methods: {
    ...mapActions('space/goals', ['createNewGoal']),
    handleGoalType() {
      if (!!this.form.account.id) {
        this.form.account = this.goalAccountsByGroup[0].childs[0];
      }
      this.form.account_type = this.accountTypeList[0];
    },
    computeGoalAchievement() {
      if (this.form.date_start && this.form.date_end && this.form.goal_sum) {
        let months = this.$moment(this.form.date_end, 'YYYY-MM')
            .diff(this.$moment(this.form.date_start, 'YYYY-MM'), 'months') + 1;
        if (months > 1200) {
          this.fixDateTo100Years()
          return false;
        }
        let monthlyPayment = Math.ceil(this.form.goal_sum / months);
        this.setComputeGoalAchievement(months, this.form.date_end, monthlyPayment);
      } else {
        this.setComputeGoalAchievement();
      }
    },
    fixDateTo100Years() {
      this.form.date_end = this.$moment(this.form.date_start, 'YYYY-MM').add(100, 'year');
    },
    setComputeGoalAchievement(months = null, finishAt = null, monthlyPayment = null) {
      this.computedGoal.months = months;
      this.computedGoal.finishAt = !!finishAt
          ? this.$moment(finishAt).format('MMMM YYYY')
          : null;
      this.computedGoal.monthlyPayment = !!monthlyPayment
          ? this.displayCurrencyValue(+monthlyPayment, this.goalCurrency, true, true)
          : null;
    },
    selectGoalImage(val) {
      this.form.goal_image = val;
      this.openImagePicker = false;
    },
    hideImagePicker() {
      this.openImagePicker = false;
    },
    async submit() {
      this.$v.$touch();
      if (!this.form.goal_image.goal_image) {
        this.$toasted.error(this.$t('pickAnImage'));
        return;
      }
      if (!this.$v.$invalid) {
        try {
          let {account, account_type, goal_type, goal_title, goal_image, goal_sum, date_start, date_end, description} = this.form;
          let data = {
            space_id: this.spaceInfo.id,
            goal_type: goal_type.id,
            goal_image: goal_image.goal_image,
            goal_title, goal_sum, date_start, date_end, description
          };

          if (account.id) {
            data.account_id = account.id;
          } else {
            data.currency_id = this.goalCurrency.id;
            data.type_id = account_type.id;
          }
          this.submited = true;
          const res = await this.createNewGoal(data);
          if (res.body.success) {
            this.$toasted.success(this.$t('goalCreatedMessage'));
            this.$emit('finish');
          } else {
            this.$toasted.error(this.$t('errorOccurred'));
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.submited = false;
        }
      }
    }
  },
  created() {
    this.form.goal_type = this.goalTypeList[0];

    this.form.account = this.goalAccountsByGroup[0].childs[0];
    this.form.account_currency = this.spaceCurrency;
    this.form.account_type = this.accountTypeList[0];

    this.form.date_start = this.$moment().format('YYYY-MM')
    this.form.date_end = this.$moment().add(1, 'month').format('YYYY-MM')
  },
  watch: {
    'form.goal_sum': {
      handler: 'computeGoalAchievement'
    },
    'form.date_start': {
      handler: 'computeGoalAchievement'
    },
    'form.date_end': {
      handler: 'computeGoalAchievement'
    },
    'form.account': {
      handler: 'computeGoalAchievement'
    },
  },
  i18n: {
    messages: {
      en: {
        formTitle: 'Create goal',
        typeLabel: 'Type',
        goalNameLabel: 'Goal name',
        goalAccount: 'Goal account',
        newAccountLabel: 'New account',
        accountCurrency: 'Account currency',
        accountType: 'Account type',
        savingAccountTip: 'If you have a separate bank account where you save only for this purpose, select it. <br> If not, create a new account. ',
        creditAccountTip: 'If you have a separate bank credit account - select it. <br> If not, create a new account.',
        warningAccountTip: 'If you close the goal, you will no longer be able to use this account. <br> If in doubt, select New account.',
        goalSumLabel: 'Goal sum',
        firstPaymentDate: 'Start',
        goalAchievementDate: 'Finish',
        noteLabel: 'Description',
        addGoal: 'Create goal',
        pickAnImage: 'Select an image',
        goalCreatedMessage: 'Goal created successfully',
        errorOccurred: 'Error occurred',
        errorSetMorePayment: 'Increase your payment: finish date is more than in 100 years'

      },
      ru: {
        formTitle: 'Добавление цели',
        typeLabel: 'Тип',
        goalNameLabel: 'Название',
        goalAccount: 'Счёт цели',
        newAccountLabel: 'Новый счёт',
        accountCurrency: 'Валюта счёта',
        accountType: 'Тип счёта',
        savingAccountTip: 'Если у вас есть отдельный банковский счёт, на котором вы копите только на эту цель, — выберите его. <br> Если такого нет, создайте новый счёт.',
        creditAccountTip: 'Если у вас есть отдельный банковский счет кредита — выберите его. <br> Если такого нет, создайте новый счет.',
        warningAccountTip: 'Если вы закроете цель, вы больше не сможете использовать этот счёт. <br> Если сомневаетесь — выберите Новый счёт. ',
        goalSumLabel: 'Сумма цели',
        firstPaymentDate: 'Старт',
        goalAchievementDate: 'Финиш',
        noteLabel: 'Заметка',
        addGoal: 'Добавить цель',
        pickAnImage: 'Выберите изображение',
        goalCreatedMessage: 'Цель успешно создана',
        errorOccurred: 'Произошла ошибка',
        errorSetMorePayment: 'Увеличьте платеж: эта цель больше чем на 100 лет'
      },
    }
  }
}
</script>

<style lang="scss" scoped>
.goal-form-block-grid {
  @apply grid;
  grid-template-columns: 1fr 0.54fr;

}
</style>